import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { LockClock as LockClockIcon } from '@mui/icons-material';
import configurationService from '../../services/configurationService';

interface InactivityLockMonitorProps {
  onLock: () => void;
}

const DEFAULT_INACTIVITY_LIMIT_MINUTES = 15;
const WARNING_LIMIT_MS = 10 * 1000;

const activityEvents = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
] as const;

const InactivityLockMonitor: React.FC<InactivityLockMonitorProps> = ({ onLock }) => {
  const theme = useTheme();
  const onLockRef = useRef(onLock);
  const inactivityLimitMsRef = useRef(DEFAULT_INACTIVITY_LIMIT_MINUTES * 60 * 1000);
  const deadlineRef = useRef(Date.now() + inactivityLimitMsRef.current);
  const lockedRef = useRef(false);
  const [secondsToLock, setSecondsToLock] = useState<number | null>(null);

  useEffect(() => {
    onLockRef.current = onLock;
  }, [onLock]);

  useEffect(() => {
    let active = true;

    const loadInactivityTimeout = async () => {
      const minutes = await configurationService.getInactivityLockTimeoutMinutes();
      if (!active || lockedRef.current) return;

      inactivityLimitMsRef.current = minutes * 60 * 1000;
      deadlineRef.current = Date.now() + inactivityLimitMsRef.current;
      setSecondsToLock(null);
    };

    loadInactivityTimeout();

    const handleTimeoutUpdated = (event: Event) => {
      const minutes = Number((event as CustomEvent<{ minutes?: number }>).detail?.minutes);
      if (!Number.isFinite(minutes) || minutes <= 0 || lockedRef.current) return;

      inactivityLimitMsRef.current = minutes * 60 * 1000;
      deadlineRef.current = Date.now() + inactivityLimitMsRef.current;
      setSecondsToLock(null);
    };

    window.addEventListener('sysup:inactivity-timeout-updated', handleTimeoutUpdated);

    return () => {
      active = false;
      window.removeEventListener('sysup:inactivity-timeout-updated', handleTimeoutUpdated);
    };
  }, []);

  useEffect(() => {
    const resetInactivityTimer = () => {
      if (lockedRef.current) return;

      deadlineRef.current = Date.now() + inactivityLimitMsRef.current;
      setSecondsToLock(null);
    };

    const checkInactivity = () => {
      if (lockedRef.current) return;

      const timeLeft = deadlineRef.current - Date.now();

      if (timeLeft <= 0) {
        lockedRef.current = true;
        setSecondsToLock(null);
        onLockRef.current();
        return;
      }

      if (timeLeft <= WARNING_LIMIT_MS) {
        setSecondsToLock(Math.max(1, Math.ceil(timeLeft / 1000)));
        return;
      }

      setSecondsToLock(null);
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer, { passive: true });
    });

    document.addEventListener('visibilitychange', checkInactivity);
    const intervalId = window.setInterval(checkInactivity, 250);

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer);
      });
      document.removeEventListener('visibilitychange', checkInactivity);
      window.clearInterval(intervalId);
    };
  }, []);

  if (secondsToLock === null) {
    return null;
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        top: { xs: 10, sm: 14 },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: theme.zIndex.tooltip + 10,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.75,
        py: 0.75,
        maxWidth: 'calc(100vw - 24px)',
        borderRadius: 1,
        border: '1px solid',
        borderColor: alpha(theme.palette.warning.main, 0.42),
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.warning.dark, 0.92)
            : alpha(theme.palette.warning.light, 0.96),
        color: theme.palette.warning.contrastText,
        boxShadow: theme.shadows[8],
        pointerEvents: 'none',
      }}
    >
      <LockClockIcon fontSize="small" />
      <Typography variant="body2" fontWeight={700} noWrap>
        bloqueio por inatividade em {secondsToLock}s
      </Typography>
    </Box>
  );
};

export default InactivityLockMonitor;
